import {
  generatePresignedUrl,
  uploadFileToS3,
  registerMediaWithBackend,
  PresignedUrlRequest,
  MediaRegistration
} from '../../../services/contentService';
import { getImageDimensions, getVideoDuration } from '../../../utils/mediaDimension';
import { sanitizeFileName } from '../../../utils/fileUtils';
import { encodeCloudFrontUrl } from '../../../utils/cloudFrontUrlHandler';
import { debug } from "../../../utils/debugLogger";

interface UploadMediaParams {
  file: File;
  customerId: string;
  userEmail: string;
  token: string;
  isLogo?: boolean;
  mediaType?: "processed" | "raw";
  additionalMetadata?: Record<string, any>;
  tags?: string[];
}

interface UploadResult {
  success: boolean;
  cloudFrontUrl: string;
  s3Key: string;
  mediaId?: string;
  errorMessage?: string;
  /** Kind actually used for the upload (MIME sniffed with an extension fallback — see inferMediaKind). */
  mediaKind?: 'image' | 'video' | 'document';
}

/**
 * Uploads a file to S3 and registers it with the backend.
 * This is a centralized service for all file uploads across the application.
 */
export const uploadMedia = async ({
  file,
  customerId,
  userEmail,
  token,
  isLogo = false,
  mediaType = "processed",
  additionalMetadata = {},
  tags = []
}: UploadMediaParams): Promise<UploadResult> => {
  try {
    const sanitizedName = sanitizeFileName(file.name);
    const uploadFile = sanitizedName === file.name ? file : new File([file], sanitizedName, { type: file.type });

    debug.log(`Uploading ${isLogo ? 'logo' : 'media'}:`, {
      fileName: uploadFile.name,
      fileType: uploadFile.type,
      fileSize: uploadFile.size,
      customerId
    });
    
    const inferMediaKind = (f: File): 'image' | 'video' | 'document' | 'unknown' => {
      if (f.type.startsWith('image/')) return 'image';
      if (f.type.startsWith('video/')) return 'video';
      if (f.type === 'application/pdf') return 'document';
      const n = f.name.toLowerCase();
      if (/\.(jpe?g|png|gif|webp|bmp|avif|heic|heif|svg)(\?|#|$)/i.test(n)) return 'image';
      if (/\.(mp4|webm|mov|m4v|mkv|avi)(\?|#|$)/i.test(n)) return 'video';
      if (/\.pdf(\?|#|$)/i.test(n)) return 'document';
      return 'unknown';
    };

    const kind = inferMediaKind(uploadFile);
    const isImage = kind === 'image';
    const isVideo = kind === 'video';
    const isDocument = kind === 'document';

    const ext = uploadFile.name.split('.').pop()?.toLowerCase() ?? '';
    const imageMimeByExt: Record<string, string> = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      webp: 'image/webp',
      bmp: 'image/bmp',
      avif: 'image/avif',
      heic: 'image/heic',
      heif: 'image/heif',
      svg: 'image/svg+xml',
    };
    const videoMimeByExt: Record<string, string> = {
      mp4: 'video/mp4',
      webm: 'video/webm',
      mov: 'video/quicktime',
      m4v: 'video/x-m4v',
      mkv: 'video/x-matroska',
      avi: 'video/x-msvideo',
    };

    const effectiveImageType =
      isImage && uploadFile.type
        ? uploadFile.type
        : isImage
          ? imageMimeByExt[ext] || 'application/octet-stream'
          : '';
    const effectiveVideoType =
      isVideo && uploadFile.type
        ? uploadFile.type
        : isVideo
          ? videoMimeByExt[ext] || 'application/octet-stream'
          : '';
    const effectiveDocumentType = isDocument ? 'application/pdf' : '';

    if (!isImage && !isVideo && !isDocument) {
      return {
        success: false,
        cloudFrontUrl: "",
        s3Key: "",
        errorMessage:
          "Unsupported file type. Use a common image (JPG, PNG, WebP, HEIC, …), video (MP4, MOV, …), or document (PDF). If the file is valid, try renaming it to include the extension.",
      };
    }

    /** S3 PUT must match signed Content-Type; some browsers omit `file.type` for images. */
    const fileForS3 =
      isImage && (!uploadFile.type || uploadFile.type === 'application/octet-stream') && effectiveImageType
        ? new File([uploadFile], uploadFile.name, { type: effectiveImageType, lastModified: uploadFile.lastModified })
        : isVideo &&
            (!uploadFile.type || uploadFile.type === 'application/octet-stream') &&
            effectiveVideoType
          ? new File([uploadFile], uploadFile.name, { type: effectiveVideoType, lastModified: uploadFile.lastModified })
          : isDocument && (!uploadFile.type || uploadFile.type === 'application/octet-stream')
            ? new File([uploadFile], uploadFile.name, { type: 'application/pdf', lastModified: uploadFile.lastModified })
            : uploadFile;

    // Generate presigned URL
    const presignedRequest: PresignedUrlRequest = {
      customer_id: customerId,
      file_name: uploadFile.name,
      user_email: userEmail,
      is_image: isImage,
      is_video: isVideo,
      is_document: isDocument,
      is_training_data: false,
      image_content_type: isImage ? effectiveImageType : '',
      video_content_type: isVideo ? effectiveVideoType : '',
      document_content_type: isDocument ? effectiveDocumentType : '',
      training_data_content_type: '',
      image_name: isImage ? uploadFile.name : '',
      video_name: isVideo ? uploadFile.name : '',
      image_type: isLogo ? "logo" : mediaType,
      video_type: isVideo ? mediaType : "",
      logo: isLogo,
    };
    
    const presignedResponse = await generatePresignedUrl(presignedRequest, token);
    
    // Upload to S3 and register with backend based on file type
    if (isImage && presignedResponse.image?.uploadURL) {
      await uploadFileToS3(presignedResponse.image.uploadURL, fileForS3);

      const imageCloudFrontUrl = encodeCloudFrontUrl(
        presignedResponse.image.cloudFrontURL || ""
      );
      
      // Get dimensions for images
      let dimensions: string | undefined = undefined;
      try {
        dimensions = await getImageDimensions(fileForS3);
      } catch (dimErr) {
        debug.error("Could not get image dimensions:", dimErr);
      }
      
      // Create media item
      const mediaItem: MediaRegistration = {
        customer_id: customerId,
        media_type: "IMAGE",
        s3_key: presignedResponse.image.fileInfo.key,
        cloudfront_url: imageCloudFrontUrl,
        metadata: {
          size: fileForS3.size,
          dimensions: dimensions,
          content_type: effectiveImageType || fileForS3.type || 'image/jpeg',
          // Store isLogo in additionalMetadata if needed
          ...additionalMetadata
        },
        created_by: userEmail,
        tags: isLogo ? [...tags, "logo"] : tags,
        processed: mediaType === "processed",
      };
      
      // Register with backend
      const registrationResponse = await registerMediaWithBackend(mediaItem, token);
      debug.log("Image registered successfully:", uploadFile.name, registrationResponse);
      
      return {
        success: true,
        cloudFrontUrl: imageCloudFrontUrl,
        s3Key: presignedResponse.image.fileInfo.key,
        mediaId: registrationResponse?.media_id || registrationResponse?.id,
        mediaKind: 'image'
      };
    }

    // Handle video uploads
    if (isVideo && presignedResponse.video?.uploadURL) {
      await uploadFileToS3(presignedResponse.video.uploadURL, fileForS3);

      const videoCloudFrontUrl = encodeCloudFrontUrl(
        presignedResponse.video.cloudFrontURL || ""
      );
      
      // Get duration for videos
      let duration = 0;
      try {
        duration = await getVideoDuration(fileForS3);
      } catch (durErr) {
        debug.error("Could not get video duration:", durErr);
      }
      
      // Create media item
      const mediaItem: MediaRegistration = {
        customer_id: customerId,
        media_type: "VIDEO",
        s3_key: presignedResponse.video.fileInfo.key,
        cloudfront_url: videoCloudFrontUrl,
        metadata: {
          size: fileForS3.size,
          duration: duration,
          content_type: effectiveVideoType || fileForS3.type || 'video/mp4',
          ...additionalMetadata
        },
        created_by: userEmail,
        tags: tags,
        processed: mediaType === "processed",
      };
      
      // Register with backend
      const registrationResponse = await registerMediaWithBackend(mediaItem, token);
      debug.log("Video registered successfully:", uploadFile.name, registrationResponse);
      
      return {
        success: true,
        cloudFrontUrl: videoCloudFrontUrl,
        s3Key: presignedResponse.video.fileInfo.key,
        mediaId: registrationResponse?.media_id || registrationResponse?.id,
        mediaKind: 'video'
      };
    }

    // Handle document uploads
    if (isDocument && presignedResponse.document?.uploadURL) {
      await uploadFileToS3(presignedResponse.document.uploadURL, fileForS3);

      const documentCloudFrontUrl = encodeCloudFrontUrl(
        presignedResponse.document.cloudFrontURL || ""
      );

      const mediaItem: MediaRegistration = {
        customer_id: customerId,
        media_type: "DOCUMENT",
        s3_key: presignedResponse.document.fileInfo.key,
        cloudfront_url: documentCloudFrontUrl,
        metadata: {
          size: fileForS3.size,
          content_type: 'application/pdf',
          ...additionalMetadata
        },
        created_by: userEmail,
        tags: tags,
        processed: mediaType === "processed",
      };

      const registrationResponse = await registerMediaWithBackend(mediaItem, token);
      debug.log("Document registered successfully:", uploadFile.name, registrationResponse);

      return {
        success: true,
        cloudFrontUrl: documentCloudFrontUrl,
        s3Key: presignedResponse.document.fileInfo.key,
        mediaId: registrationResponse?.media_id || registrationResponse?.id,
        mediaKind: 'document'
      };
    }
    
    return {
      success: false,
      cloudFrontUrl: "",
      s3Key: "",
      errorMessage: isImage
        ? "No image upload URL was returned. The presigned-url service may be misconfigured or rejected this file."
        : isVideo
          ? "No video upload URL was returned. The presigned-url service may be misconfigured or rejected this file."
          : "Unsupported file type",
    };
  } catch (err) {
    debug.error("Media upload failed:", err);
    return {
      success: false,
      cloudFrontUrl: "",
      s3Key: "",
      errorMessage: err instanceof Error ? err.message : "Unknown error"
    };
  }
};